import { Evaluation, Funder, Recommender } from "@engine/round/aggregate";
import React, { useState } from "react";

import { Tooltip } from "@client/components/utils/Tooltip";
import InfoIcon from "@client/icons/InfoIcon";
import { Evaluable } from "@lib/types";
import { AllocationChartItemData } from "../AllocationChart/AllocationChart";
import { EvaluableCard } from "../EvaluableCard/EvaluableCard";
import { FrontendEvaluation } from "../useEvaluatorEvaluationsCache";

export function Evaluables({
  evaluator,
  allocations,
  evaluatedEvaluables,
  recusedEvaluables,
  unusedEvaluables,
  hiddenEvaluables,
  deferredEvaluables,
  zeroedEvaluables,
}: {
  evaluator: Recommender | Funder;
  allocations: AllocationChartItemData[];
  evaluatedEvaluables: Evaluable[];
  recusedEvaluables: Evaluable[];
  unusedEvaluables: Evaluable[];
  hiddenEvaluables: Evaluable[];
  deferredEvaluables: Evaluable[];
  zeroedEvaluables: Evaluable[];
}) {
  const [stickyHeader, setStickyHeader] = useState<
    | "evaluated"
    | "not-evaluated"
    | "recused"
    | "hidden"
    | "deferred"
    | "inactive"
    | "zeroed"
    | null
  >(null);
  const inactiveRecommenders: Recommender[] = [];

  const headers = [
    ["evaluated", evaluatedEvaluables],
    ["not-evaluated", unusedEvaluables],
    ["deferred", deferredEvaluables],
    ["recused", recusedEvaluables],
    ["zeroed", zeroedEvaluables],
    ["hidden", hiddenEvaluables],
  ] as const;

  function handleScroll(e: React.UIEvent<HTMLDivElement, UIEvent>) {
    let currentSidebarHeight = 0;
    for (const [header, evaluables] of headers) {
      currentSidebarHeight += (evaluables.length + 1) * 30;
      if (e.currentTarget.scrollTop < currentSidebarHeight) {
        setStickyHeader(header);
        break;
      }
    }
  }

  // SP-375: Filtering out users who don't have MVFs
  if (evaluator.type === "funder") {
    const recommenderEvaluables = unusedEvaluables as Recommender[];
    unusedEvaluables = recommenderEvaluables.filter((evaluation) => {
      const isActive = Object.values(evaluation.evaluations).length > 1;
      if (!isActive) {
        inactiveRecommenders.push(evaluation);
      }
      return isActive && !evaluation.shortname.toLowerCase().includes("mean");
    });
  }

  function getActiveEvaluation(
    evaluable: Evaluable,
  ): FrontendEvaluation | undefined {
    return allocations.find(
      (allocation) => allocation.evaluableId === evaluable.id,
    )?.evaluation;
  }

  function getInactiveEvaluation(evaluable: Evaluable): Evaluation | undefined {
    return evaluator.evaluations[evaluable.id];
  }

  function getEvaluation(
    evaluable: Evaluable,
  ): FrontendEvaluation | Evaluation | undefined {
    return getActiveEvaluation(evaluable) || getInactiveEvaluation(evaluable);
  }

  return (
    <div className="relative min-h-0 h-full">
      <div
        className="overflow-x-hidden overflow-y-auto h-full"
        onScroll={handleScroll}
      >
        <StickyHeader isSticky={stickyHeader === "evaluated"}>
          <Header>Evaluated</Header>
        </StickyHeader>
        {evaluatedEvaluables.map((evaluable) => (
          <EvaluableCard
            evaluator={evaluator}
            evaluable={evaluable}
            evaluation={getActiveEvaluation(evaluable)}
            key={evaluable.id}
          />
        ))}
        <StickyHeader isSticky={stickyHeader === "not-evaluated"}>
          <Header>Not Evaluated</Header>
        </StickyHeader>
        {unusedEvaluables.map((evaluable) => (
          <EvaluableCard
            evaluator={evaluator}
            evaluable={evaluable}
            evaluation={getInactiveEvaluation(evaluable)}
            key={evaluable.id}
          />
        ))}
        <StickyHeader isSticky={stickyHeader === "deferred"}>
          <Header>Deferred</Header>
        </StickyHeader>
        {deferredEvaluables.map((evaluable) => (
          <EvaluableCard
            evaluator={evaluator}
            evaluable={evaluable}
            evaluation={getInactiveEvaluation(evaluable)}
            key={evaluable.id}
          />
        ))}
        <StickyHeader isSticky={stickyHeader === "recused"}>
          <Header>Recused</Header>
        </StickyHeader>
        {recusedEvaluables.map((evaluable) => (
          <EvaluableCard
            evaluator={evaluator}
            evaluable={evaluable}
            evaluation={getInactiveEvaluation(evaluable)}
            key={evaluable.id}
          />
        ))}
        <StickyHeader isSticky={stickyHeader === "zeroed"}>
          <Header>Zeroed</Header>
        </StickyHeader>
        {zeroedEvaluables.map((evaluable) => (
          <EvaluableCard
            evaluator={evaluator}
            evaluable={evaluable}
            evaluation={getInactiveEvaluation(evaluable)}
            key={evaluable.id}
          />
        ))}
        <StickyHeader isSticky={stickyHeader === "hidden"}>
          <Header>Hidden</Header>
        </StickyHeader>
        {hiddenEvaluables.map((evaluable) => (
          <EvaluableCard
            evaluator={evaluator}
            evaluable={evaluable}
            evaluation={getInactiveEvaluation(evaluable)}
            key={evaluable.id}
          />
        ))}
        {inactiveRecommenders.length > 0 && (
          <>
            <StickyHeader isSticky={stickyHeader == "inactive"}>
              <Header>
                Inactive{" "}
                <Tooltip title="These recommenders have no evaluations yet.">
                  <span className="inline-block align-middle">
                    <InfoIcon />
                  </span>
                </Tooltip>
              </Header>
            </StickyHeader>
            {inactiveRecommenders.map((evaluable) => (
              <EvaluableCard
                evaluator={evaluator}
                evaluable={evaluable}
                evaluation={getEvaluation(evaluable)}
                key={evaluable.id}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

const StickyHeader = ({
  isSticky,
  children,
}: {
  isSticky: boolean;
  children: React.ReactNode;
}) => {
  return (
    <div>
      {children}
      {isSticky && (
        <div className="absolute top-0 z-20 w-full bg-black">
          {React.cloneElement(<div>{children}</div>)}
        </div>
      )}
    </div>
  );
};

const Header = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="border-b border-blue-600 w-full text-center bg-stone-200 dark:bg-inherit">
      {children}
    </div>
  );
};
